import { accountCodeSegments } from './accountCode.js'

/**
 * Aliases and code-segment distinctiveness — REQUIREMENTS.md §7.6.
 *
 * Aliases are how lookup works, so ambiguity here makes lookup ambiguous. Two
 * rules follow from that: aliases are unique within a user's fiscal year, and
 * auto-suggestions never propose a segment that fails to narrow anything.
 *
 * ## Why distinctiveness is positional
 *
 * All ten of Matt's main-fund codes end in segment 8 = `329`:
 *
 *   10-2818-329-000-00-000-000-329-0000   Prof. Ed. Services
 *   10-2818-360-000-00-000-000-329-0000   Prof. Dev. Fees
 *   10-2818-580-000-00-000-000-329-0000   Travel
 *                     ^^^ varies          ^^^ identical across all ten
 *
 * A naive "match any segment" rule returns all ten for `329`, which is useless
 * at a vendor. But `329` is *also* the distinctive third segment of Prof. Ed.
 * Services. So distinctiveness is evaluated per (position, value) pair, not per
 * value: `329` at position 3 identifies one code and is useful; `329` at
 * position 8 identifies ten and is suppressed.
 *
 * Required behavior, confirmed with Matt:
 *   329 → Prof. Ed. Services    360 → Prof. Dev. Fees    580 → Travel
 */

/**
 * A (position, value) pair is distinctive when it identifies at most half the
 * codes. Half rather than "exactly one" so the rule still helps in small sets:
 * with two codes, a segment held by one of them is genuinely useful.
 */
const DISTINCTIVE_MAX_SHARE = 0.5

/** Minimal shape needed to reason about a code. */
export interface CodeLike {
  code: string
  aliases: readonly string[]
}

/**
 * Index of how many codes share each (segment position, value) pair.
 * Key format: `position:value`.
 */
export type SegmentIndex = ReadonlyMap<string, number>

const segmentKey = (position: number, value: string): string => `${position}:${value}`

/** Build the segment-frequency index for a set of codes. */
export function buildSegmentIndex(codes: readonly CodeLike[]): SegmentIndex {
  const index = new Map<string, number>()
  for (const { code } of codes) {
    accountCodeSegments(code).forEach((value, position) => {
      const key = segmentKey(position, value)
      index.set(key, (index.get(key) ?? 0) + 1)
    })
  }
  return index
}

/**
 * True when `value` at `position` narrows the set usefully. Also requires that
 * it not match every code — a segment shared by all identifies nothing.
 */
export function isDistinctiveSegment(
  index: SegmentIndex,
  position: number,
  value: string,
  totalCodes: number,
): boolean {
  if (totalCodes === 0) return false
  const count = index.get(segmentKey(position, value)) ?? 0
  if (count === 0 || count === totalCodes) return false
  return count <= totalCodes * DISTINCTIVE_MAX_SHARE
}

/**
 * The distinctive segment values of one code — the tokens that will actually
 * find it. For Prof. Ed. Services this yields `["329"]` (position 3), not the
 * `329` at position 8 that every main-fund code shares.
 */
export function distinctiveSegmentsOf(
  code: string,
  index: SegmentIndex,
  totalCodes: number,
): string[] {
  const seen = new Set<string>()
  accountCodeSegments(code).forEach((value, position) => {
    if (isDistinctiveSegment(index, position, value, totalCodes)) seen.add(value)
  })
  return [...seen]
}

/**
 * Candidate aliases for a new code (§7.6 step 3, §7.9 step 2).
 *
 * Suggests only distinctive segments, and only ones not already claimed. The
 * user can accept, edit, or ignore them.
 */
export function suggestAliases(
  code: string,
  existingCodes: readonly CodeLike[],
): string[] {
  const all = [...existingCodes, { code, aliases: [] }]
  const index = buildSegmentIndex(all)
  const taken = new Set(
    existingCodes.flatMap((c) => c.aliases.map((a) => a.toLowerCase())),
  )
  return distinctiveSegmentsOf(code, index, all.length)
    .filter((segment) => !taken.has(segment.toLowerCase()))
    .sort()
}

/** Why an alias was rejected, or null when it's acceptable. */
export type AliasRejection =
  | { reason: 'empty' }
  | { reason: 'invalid_characters' }
  | { reason: 'too_long' }
  | { reason: 'duplicate'; conflictingDisplayName: string }

export interface NamedCodeLike extends CodeLike {
  id?: string | undefined
  displayName: string
}

/**
 * Validate a proposed alias against every other code in the same user and
 * fiscal year — owned and external alike, since both feed the same picker.
 *
 * Duplicates are refused at creation time rather than resolved with a "did you
 * mean?" prompt on every lookup (§7.6 design note).
 */
export function validateAlias(
  proposed: string,
  existingCodes: readonly NamedCodeLike[],
  options: { excludeCodeId?: string | undefined } = {},
): AliasRejection | null {
  const trimmed = proposed.trim()
  if (trimmed === '') return { reason: 'empty' }
  if (trimmed.length > 32) return { reason: 'too_long' }
  if (!/^[a-z0-9][a-z0-9-]*$/i.test(trimmed)) return { reason: 'invalid_characters' }

  const folded = trimmed.toLowerCase()
  const conflict = existingCodes.find(
    (c) =>
      c.id !== options.excludeCodeId &&
      c.aliases.some((a) => a.toLowerCase() === folded),
  )
  if (conflict) {
    return { reason: 'duplicate', conflictingDisplayName: conflict.displayName }
  }
  return null
}

/** Human-readable message for an alias rejection. */
export function aliasRejectionMessage(rejection: AliasRejection): string {
  switch (rejection.reason) {
    case 'empty':
      return 'Enter an alias.'
    case 'too_long':
      return 'Aliases should be short enough to type quickly — 32 characters max.'
    case 'invalid_characters':
      return 'Aliases may contain letters, numbers, and hyphens.'
    case 'duplicate':
      return `"${rejection.conflictingDisplayName}" already uses that alias. Pick a different one so lookup stays unambiguous.`
  }
}
